import React, {
  ReactNode,
  createContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { MatxLoading } from "app/components";
import {
  loginAuth,
  registerAuth,
  forgotPasswordAuth,
  resetPasswordAuth,
  activateAccounteAuth,
} from "app/services/authServices/auth";
import { setCookie } from "app/data/cookiesTools";
import * as storage from "app/data/storage";

interface User {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  profiles: Profile[];
  username?: string;
}

interface Profile {
  id: number;
  name: string;
  role: string;
  created_at: string;
  updated_at: string | null;
  pivot: {
    user_id: number;
    profile_id: number;
  };
}

interface AuthState {
  user: User | null;
  isInitialised: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType {
  user: User | null;
  isInitialised: boolean;
  isAuthenticated: boolean;
  method: string;
  login: (
    email: string,
    password: string,
    cookieToken: string
  ) => Promise<void>;
  logout: () => void;
  register: (
    name: string,
    email: string,
    cpf: string,
    password: string
  ) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (
    token: string,
    password: string,
    retypePassword: string
  ) => Promise<void>;
  activateAccount: (token: string) => Promise<void>;
}

interface TokenPayload extends JwtPayload {
  id: number;
  name: string;
  email: string;
  username: string;
  profiles: Profile[];
}

const initialState: AuthState = {
  user: null,
  isInitialised: false,
  isAuthenticated: false,
};

const setSession = (
  accessToken: string,
  refreshToken: string,
  profiles?: Profile[]
): void => {
  if (accessToken) {
    storage.set("accessToken", accessToken);
    storage.set("refreshToken", refreshToken);
  } else {
    storage.remove("accessToken");
    storage.remove("refreshToken");
  }
};

const decodeToken = (accessToken: string): User => {
  const decodedToken = jwtDecode<TokenPayload>(accessToken);
  const { id, name, profiles, email }: User = decodedToken;

  return {
    id,
    name,
    email,
    profiles: profiles,
  };
};

export const getUserProfiles = (): Profile | [] => {
  if (storage.get("accessToken")) {
    const userdata = decodeToken(storage.get("accessToken") || "");
    return userdata.profiles as unknown as Profile;
  } else {
    return [];
  }
};

export const setUserRole = (
  profiles: Profile,
  role?: string,
  roleName?: string
): void => {
  const storedRole = storage.get("role");

  if (storedRole && profiles) {
    let roleExists = false;

    for (const key in profiles) {
      if (profiles.hasOwnProperty(key)) {
        const profile = profiles[key];
        if (profile.role === storedRole) {
          roleExists = true;
          break;
        }
      }
    }
    if (roleExists) {
      if (role && roleName) {
        storage.set("role", role);
        storage.set("roleName", roleName);
      }
    } else {
      storage.set("role", profiles[0].role);
      storage.set("roleName", profiles[0].name);
    }
  }

  if (!storedRole) {
    storage.set("role", profiles[0].role);
    storage.set("roleName", profiles[0].name);
  }
};

const reducer = (
  state: AuthState,
  action: { type: string; payload?: any }
): AuthState => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user } = action.payload;
      return { ...state, isAuthenticated, isInitialised: true, user };
    }

    case "LOGIN": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: true, user };
    }

    case "LOGOUT": {
      return { ...state, isAuthenticated: false, user: null };
    }

    case "REGISTER": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: false, user };
    }

    case "FORGOTPASSWORD": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: false, user };
    }

    case "RESETPASSWORD": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: false, user };
    }

    case "ACTIVATEACCOUNT": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: false, user };
    }

    default:
      return state;
  }
};

export const AuthContext = createContext({} as AuthContextType);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [user, setUser] = useState<User>({} as User);

  const login = async (
    email: string,
    password: string,
    cookieToken: string
  ): Promise<void> => {
    const jwtToken = await loginAuth(email, password, cookieToken);
    const { response } = jwtToken.data;
    const user = decodeToken(response);

    dispatch({ type: "LOGIN", payload: { user } });
    //setUserRole(user.profiles as Profile)
    storage.remove("role");
    storage.remove("roleName");
    setSession(response, response);
    setCookie(email, cookieToken);
  };

  const register = async (
    name: string,
    email: string,
    cpf: string,
    password: string
  ): Promise<void> => {
    const response = await registerAuth(name, email, cpf, password);
    const { user } = response.data;

    dispatch({ type: "REGISTER", payload: { user } });
  };

  const forgotPassword = async (email: string) => {
    const response = await forgotPasswordAuth(email);
    const { user } = response.data;

    dispatch({ type: "FORGOTPASSWORD", payload: { user } });
  };

  const resetPassword = async (
    token: string,
    password: string,
    retypePassword: string
  ) => {
    const response = await resetPasswordAuth(token, password, retypePassword);
    const { user } = response.data;

    dispatch({ type: "RESETPASSWORD", payload: { user } });
  };

  const activateAccount = async (token: string) => {
    const response = await activateAccounteAuth(token);
    const { user } = response.data;

    dispatch({ type: "ACTIVATEACCOUNT", payload: { user } });
  };

  const logout = async () => {
    setSession("", "");
    dispatch({ type: "LOGOUT" });
  };

  useEffect(() => {
    (async () => {
      const storedAccessToken = storage.get("accessToken");
      if (storedAccessToken) {
        const user = decodeToken(storedAccessToken);

        setUser(user);

        dispatch({
          type: "INIT",
          payload: { isAuthenticated: true, user: user },
        });
      } else {
        dispatch({
          type: "INIT",
          payload: { isAuthenticated: false, user: null },
        });
        //navigate('session/signin')
      }
    })();
  }, []);

  if (!state.isInitialised) return <MatxLoading />;

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        logout,
        register,
        forgotPassword,
        resetPassword,
        activateAccount,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
